import { ActionButton, ActionButtonType } from "@octopusdeploy/design-system-components";
import * as React from "react";
import { useAnalyticSimpleActionDispatch } from "~/analytics/Analytics";
import Card from "~/components/Card";
import { useDialogTrigger, DialogTrigger } from "~/components/Dialog/DialogTrigger";
import { isFeatureToggleEnabled } from "~/components/FeatureToggle/New/FeatureToggleContext";
import { UpsellDialogWithLicenseUsage } from "~/components/UpsellDialog/UpsellDialogWithLicenseUsage";
import { EntitlementLimitPopUpEvent } from "~/components/UpsellDialog/upsellDialogAnalytics";
import { calculateDeploymentTargetLimits } from "~/hooks/useDeploymentTargetLimit";
import type { CommunicationStyle } from "../../../../../../../../packages/octopus-server-client/src/index";
import styles from "./EndpointCard.module.less";
import { useDeploymentTargetLimitsContext } from "./EndpointSelector";
export interface EndpointCardDispatchProps {
    onNavigate?: () => void;
    onClick?: () => void;
}
export interface EndpointCardDetailProps {
    logo: React.ReactElement<{}>;
    registrationName: string;
    description: React.ReactNode;
    buttonLabel?: string;
    communicationStyle: CommunicationStyle;
    showUpsellExperience?: boolean;
}
export type EndpointCardProps = EndpointCardDispatchProps & EndpointCardDetailProps;
export const EndpointCard: React.FC<EndpointCardProps> = (props) => (<EndpointLicenseUpsellGuard showUpsellExperience={props.showUpsellExperience}>
        {(guardOnClick, dispatchPopUpEvent) => {
        const conditionalOnClick = () => {
            if (!guardOnClick(props.communicationStyle)) {
                dispatchPopUpEvent(); // Only dispatch if we are blocking the action
                return;
            }
            if (props.onClick)
                props.onClick();
        };
        const conditionalNavigate = () => {
            if (!guardOnClick(props.communicationStyle))
                return;
            if (props.onNavigate)
                props.onNavigate();
        };
        return (<Card logo={<div className={styles.logo}>{props.logo}</div>} className={styles.card} header={props.registrationName} content={<div className={styles.description}>{props.description}</div>} includeContentFade={false} /* Don't set true here, or you see the contentFade onHover */ onHoverView={<div className={styles.onHoverActions}>
                            <div className={styles.onHoverActionsBackdrop}/>
                            <ActionButton label={props.buttonLabel ? props.buttonLabel : "Add"} accessibleName={`Add ${props.registrationName}`} type={ActionButtonType.Primary} onClick={conditionalNavigate}/>
                        </div>} onClick={conditionalOnClick}/>);
    }}
    </EndpointLicenseUpsellGuard>);
EndpointCard.displayName = "EndpointCard"
export interface EndpointCardDialogProps {
    open: boolean;
    closeDialog(): void;
    openDialog(): void;
}
interface EndpointDialogCardDetailProps {
    logo: React.ReactElement<{}>;
    registrationName: string;
    description: React.ReactNode;
    buttonLabel?: string;
    renderDialog: (props: EndpointCardDialogProps) => React.ReactNode;
    chip?: React.ReactNode;
    onClick?: () => void;
    communicationStyle: CommunicationStyle;
    showUpsellExperience?: boolean;
}
export const EndpointDialogCard = (props: EndpointDialogCardDetailProps) => {
    const { isOpen, openDialog, closeDialog } = useDialogTrigger();
    return (<EndpointLicenseUpsellGuard showUpsellExperience={props.showUpsellExperience}>
            {(guardOnClick, dispatchPopUpEvent) => {
            const conditionalOnClick = () => {
                if (!guardOnClick(props.communicationStyle)) {
                    dispatchPopUpEvent(); // Only dispatch if we are blocking the action
                    return;
                }
                if (isOpen)
                    return;
                if (props.onClick)
                    props.onClick();
                openDialog();
            };
            return (<>
                        <Card logo={<div className={styles.logo}>{props.logo}</div>} className={styles.card} header={props.registrationName} content={<div>
                                    <div className={styles.description}>{props.description}</div>
                                    <div className={styles.chip}>{props.chip}</div>
                                </div>} contentClassName={props.chip ? styles.variableHeightContent : undefined} includeContentFade={false} /* Don't set true here, or you see the contentFade onHover */ onHoverView={<div className={styles.onHoverActions}>
                                    <div className={styles.onHoverActionsBackdrop}/>
                                    <ActionButton label={props.buttonLabel ? props.buttonLabel : "Add"} accessibleName={`Add ${props.registrationName}`} type={ActionButtonType.Primary} onClick={conditionalOnClick}/>
                                </div>}/>
                        {props.renderDialog({ open: isOpen, openDialog, closeDialog })}
                    </>);
        }}
        </EndpointLicenseUpsellGuard>);
};
interface InformationalEndpointCardRenderProps {
    open: boolean;
    closeDialog(): void;
    openDialog(): void;
}
export type InformationalEndpointCardProps = EndpointCardDetailProps & {
    renderDialog: (props: InformationalEndpointCardRenderProps) => React.ReactElement<{}>;
    onClick?: () => void;
};
export const InformationalEndpointCard: React.FC<InformationalEndpointCardProps> = ({ buttonLabel = "View Instructions", renderDialog, onClick, ...rest }) => (<EndpointLicenseUpsellGuard showUpsellExperience={rest.showUpsellExperience}>
        {(guardOnClick, dispatchPopUpEvent) => (<DialogTrigger render={(renderProps) => {
            const conditionalOnClick = () => {
                if (!guardOnClick(rest.communicationStyle)) {
                    dispatchPopUpEvent(); // Only dispatch if we are blocking the action
                    return;
                }
                if (renderProps.open)
                    return;
                if (onClick)
                    onClick();
            };
            return (<React.Fragment>
                            <EndpointCard {...rest} onNavigate={renderProps.openDialog} buttonLabel={buttonLabel} onClick={conditionalOnClick}/>
                            {renderDialog(renderProps)}
                        </React.Fragment>);
        }}/>)}
    </EndpointLicenseUpsellGuard>);
InformationalEndpointCard.displayName = "InformationalEndpointCard"
interface EndpointLicenseUpsellGuardProps {
    children: (conditionalOnClick: (communicationStyle: CommunicationStyle) => boolean, dispatchPopUpEvent: () => void) => React.ReactNode;
    showUpsellExperience?: boolean;
}
const EndpointLicenseUpsellGuard = ({ children, showUpsellExperience }: EndpointLicenseUpsellGuardProps) => {
    const { licenseUsage, licenseStatus } = useDeploymentTargetLimitsContext();
    const targetLimits = calculateDeploymentTargetLimits(licenseUsage, licenseStatus);
    const { isOpen: isTargetLimitDialogOpen, openDialog: openTargetLimitDialog, closeDialog: closeTargetLimitDialog } = useDialogTrigger();
    const [licensedLimit, setLicensedLimit] = React.useState<number>(0);
    const isFreeTierEnabled = isFeatureToggleEnabled("free-tier");
    const simpleDispatchAction = useAnalyticSimpleActionDispatch();
    const conditionalOnClick = (communicationStyle: CommunicationStyle) => {
        if (targetLimits.get(communicationStyle)?.isLimitReached && isFreeTierEnabled && showUpsellExperience) {
            setLicensedLimit(targetLimits.get(communicationStyle)?.licensedLimit || 0);
            openTargetLimitDialog();
            return false;
        }
        return true;
    };
    // This is to precisely control when to send the event because conditionalOnClick is triggerred from multiple layers -- ie: from the ActionButton onNavigate and from the Card onClick.
    const dispatchPopUpEvent = () => {
        simpleDispatchAction(EntitlementLimitPopUpEvent);
    };
    return (<>
            <>{children(conditionalOnClick, dispatchPopUpEvent)}</>
            {showUpsellExperience && isFreeTierEnabled && (<UpsellDialogWithLicenseUsage title="Need more machines?" content={<>
                            You've reached your <strong>{licensedLimit}-machine</strong> limit. Upgrade to add more.
                        </>} open={isTargetLimitDialogOpen} onClose={closeTargetLimitDialog} licenseSerialNumber={licenseStatus?.SerialNumber} licenseUsage={licenseUsage}/>)}
        </>);
};
export default EndpointCard;
